import { useEffect, useId, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";

import type { School } from "@canvaschool/contracts";
import type { KeyboardEvent } from "react";

const DEBOUNCE_MILLISECONDS = 300;
const MIN_QUERY_LENGTH = 2;
const MAX_QUERY_LENGTH = 40;

type SearchState = "idle" | "loading" | "done" | "error";

export type SchoolPickerProps = {
  /** Id of the text input, so a `<label htmlFor>` names it. */
  id: string;
  value: School | null;
  onChange(school: School | null): void;
  /** Search text to start from (e.g. the school typed on an application). */
  initialQuery?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
};

/** Looks a school up in the NEIS list; the pick is a real school code. */
export const SchoolPicker = ({
  id,
  value,
  onChange,
  initialQuery = "",
  disabled = false,
  autoFocus = false,
  placeholder = "학교 이름 검색 (예: 성거초)",
}: SchoolPickerProps) => {
  const { api } = useAuth();
  const listId = `${id}-list`;
  const statusId = useId();
  const [query, setQuery] = useState(() =>
    initialQuery.trim().slice(0, MAX_QUERY_LENGTH),
  );
  const [schools, setSchools] = useState<School[]>([]);
  const [state, setState] = useState<SearchState>("idle");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const refocus = useRef(false);

  const text = query.trim();
  const searchable = text.length >= MIN_QUERY_LENGTH;

  useEffect(() => {
    if (value || !searchable) {
      setState("idle");
      setSchools([]);
      setActive(-1);
      return undefined;
    }
    setState("loading");
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      api
        .searchSchools(text.slice(0, MAX_QUERY_LENGTH), controller.signal)
        .then((found) => {
          if (controller.signal.aborted) {
            return;
          }
          setSchools(found);
          setActive(-1);
          setState("done");
          setOpen(true);
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setSchools([]);
            setState("error");
            setOpen(true);
          }
        });
    }, DEBOUNCE_MILLISECONDS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [api, text, searchable, value]);

  useEffect(() => {
    if (!value && refocus.current) {
      refocus.current = false;
      inputRef.current?.focus();
    }
  }, [value]);

  const choose = (school: School) => {
    setOpen(false);
    onChange(school);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (schools.length === 0) {
        return;
      }
      event.preventDefault();
      setOpen(true);
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) =>
        current === -1
          ? step === 1
            ? 0
            : schools.length - 1
          : (current + step + schools.length) % schools.length,
      );
    } else if (event.key === "Enter") {
      // Enter picks the highlighted school; it never submits the form.
      if (open && active >= 0 && schools[active]) {
        event.preventDefault();
        choose(schools[active]);
      } else if (open && state !== "idle") {
        event.preventDefault();
      }
    } else if (event.key === "Escape" && open) {
      // Closes the list only; a surrounding dialog stays open.
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      setOpen(false);
    }
  };

  if (value) {
    return (
      <div className="canvaschool-school-picked">
        <p role="status">
          선택한 학교: {value.name}
          {value.officeName ? ` (${value.officeName})` : ""}
        </p>
        <button
          type="button"
          className="canvaschool-btn-secondary"
          disabled={disabled}
          onClick={() => {
            refocus.current = true;
            onChange(null);
          }}
        >
          바꾸기
        </button>
      </div>
    );
  }

  const listVisible = open && state === "done" && schools.length > 0;
  const message =
    state === "loading"
      ? "찾는 중…"
      : state === "error"
      ? "학교를 찾지 못했습니다. 잠시 후 다시 입력해 주세요."
      : state === "done" && schools.length === 0
      ? "검색 결과가 없습니다. 학교 이름 일부만 넣어 보세요. 예: 성거"
      : !searchable && text.length > 0
      ? "두 글자 이상 입력해 주세요."
      : null;

  return (
    <div className="canvaschool-school-picker">
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={listVisible}
        aria-controls={listId}
        aria-activedescendant={
          listVisible && active >= 0 ? `${id}-opt-${active}` : undefined
        }
        aria-describedby={message ? statusId : undefined}
        autoComplete="off"
        autoFocus={autoFocus}
        maxLength={MAX_QUERY_LENGTH}
        placeholder={placeholder}
        disabled={disabled}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      <ul
        id={listId}
        role="listbox"
        aria-label="학교 검색 결과"
        className="canvaschool-school-list"
        hidden={!listVisible}
      >
        {listVisible &&
          schools.map((school, index) => (
            <li
              key={school.code}
              id={`${id}-opt-${index}`}
              role="option"
              aria-selected={index === active}
              className={
                index === active
                  ? "canvaschool-school-option canvaschool-school-option--active"
                  : "canvaschool-school-option"
              }
              // Keep focus in the input so blur does not close the list first.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(school)}
            >
              <strong>{school.name}</strong>
              <small>
                {[school.officeName, school.address]
                  .filter((part) => part !== "")
                  .join(" · ")}
              </small>
            </li>
          ))}
      </ul>
      <p
        id={statusId}
        role="status"
        className={
          state === "error"
            ? "canvaschool-error"
            : "canvaschool-hint canvaschool-school-status"
        }
      >
        {message}
      </p>
    </div>
  );
};
